/**
 * Settings Type Definitions
 * 
 * Comprehensive type system for the settings module with proper union types
 * instead of 'any' usage for better type safety.
 * 
 * This module is organized into sections:
 * 1. Constants & Enums - Settings categories, platforms
 * 2. Persistence Interface - Platform-agnostic persistence contract
 * 3. Value Types - Union types for setting values and control types
 * 4. Control Options - Types for UI control options (switches, selects, etc.)
 * 5. Constraints - Numeric and backend constraints for validation
 * 6. Setting Configuration - Base and discriminated union types for settings
 * 7. Backend Types - Raw backend setting structure
 */

import { UserSettings, UserSettingPath } from './userSettingsDefinition';
import { ModelInfo } from './models';

// ============================================================================
// Section 1: Constants & Enums
// ============================================================================

export const SETTINGS_CATEGORY = {
    GENERAL: 'General',
    APPLICATION: 'Application',
    CHAT: 'Chat',
    NOTIFICATIONS: 'Notifications',
    EXPORT: 'Export',
    SYSTEM: 'System',
    TOOLS: 'Tools',
} as const;

export type SettingsCategory = typeof SETTINGS_CATEGORY[keyof typeof SETTINGS_CATEGORY];

export type SettingsPlatform = 'electron' | 'web';

// ============================================================================
// Section 2: Persistence Interface
// Platform-agnostic contract for settings persistence
// ============================================================================

export interface ISettingsPersistence {
	getPlatform(): SettingsPlatform;
	loadSettings(): Promise<UserSettings>;

	/**
	 * Persists a partial update and returns the new FULL settings object.
	 * Side effects: writes to disk (electron) or storage (web).
	 */
	writeSettings(partial: Partial<UserSettings>): Promise<UserSettings>;

	/**
	 * Resets a top-level key to default (electron can delegate to IPC; web can emulate).
	 * Returns the new FULL settings object.
	 */
	resetTopLevelSetting(key: keyof UserSettings): Promise<UserSettings>;
}

// ============================================================================
// Section 3: Value Types
// Union types for setting values and control types
// ============================================================================

/** Union type for all possible setting values - eliminates 'any' usage */
export type SettingValue = 
    | string 
    | number 
    | boolean 
    | string[]
    | Record<string, boolean> // For multiCheckbox
    | null
    | undefined;

// Union type for control types with exhaustive coverage
export type ControlType = 
    | 'switch' 
    | 'checkbox' 
    | 'multiCheckbox'
    | 'text' 
    | 'textbox'
    | 'select' 
    | 'multiSelect' 
    | 'secureInput' 
    | 'directoryPicker' 
    | 'dualInput'
    | 'numberInput'
    | 'info'
    | 'infoAction';

// Union type for backend data types
export type BackendDataType = 'bool' | 'string' | 'number' | 'list' | 'object';

/** Union type for options source - hybrid setting support */
export type OptionsSource = 'static' | 'backend';

// ============================================================================
// Section 4: Control Options
// Types for UI control options (switches, selects, checkboxes)
// ============================================================================

/** Switch option structure with proper typing */
export interface SwitchOption {
    value: boolean | string | number;
    label: string;
}

// Select option structure 
export interface SelectOption {
    value: string;
    label: string;
}

// Checkbox option for multi-checkbox controls
export interface CheckboxOption {
    key: string;
    label: string;
    default: boolean;
    /** Optional group label — options with the same group render inside a collapsible accordion section. */
    group?: string;
    /** Optional tooltip text shown on hover. */
    tooltip?: string;
}

/** Backend options configuration for hybrid settings */
export interface BackendOptionsConfig {
    endpoint: string; // source
    transformType: 'models' | 'generic';
    fallbackValue?: string;
}

// ============================================================================
// Section 5: Constraints
// Numeric and backend constraints for validation
// ============================================================================

/** Numeric constraints for dual input controls */
export interface NumericConstraints {
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
}


export interface BackendConstraints {
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    
    choices?: string[] | SelectOption[];
    
    display?: {
        type: 'textbox' | 'slider' | 'numberInput' | 'select' | 'multiSelect' | 'switch' | 'checkbox';
        label?: string;
        description?: string;
        placeholder?: string;
    };
}

// ============================================================================
// Section 6: Setting Configuration Types
// Base and discriminated union types for settings
// ============================================================================

/** Base setting properties shared by all setting types */
export interface BaseSetting {
    key: string;
    label: string;
    category: string;
    description?: string;
    requiresRestart?: boolean;
    editable?: boolean;
    isClientConfig?: boolean; // TODO : move to unique type (Client, Backend, UserPreferences?)
    showReset?: boolean;
    /** Controls how the label/description and input are arranged.
     *  - 'inline' (default): label left, control right — suits compact controls.
     *  - 'stacked': label above, control below full-width — suits wide/tall controls
     *    like multiCheckbox, and scales naturally to narrow/mobile viewports. */
    layout?: 'inline' | 'stacked';
}

// Base setting config with properly typed unions instead of 'any'
export interface BaseSettingConfig extends BaseSetting {
    type: BackendDataType;
    controlType: ControlType;
    default: SettingValue;
}

// Discriminated union for different control types with specific value types
export type SettingConfig = BaseSettingConfig & 
    (
        | { 
            controlType: 'switch'; 
            optionA: SwitchOption; 
            optionB: SwitchOption;
            default: boolean | string | number;
        }
        | { 
            controlType: 'select'; 
            optionsSource: 'static';
            options: SelectOption[]; 
            default: string;
        }
        | { 
            controlType: 'select'; 
            optionsSource: 'backend';
            backendOptionsConfig: BackendOptionsConfig;
            options?: SelectOption[]; // Populated at runtime
            default: string;
        }
        | { 
            controlType: 'multiSelect'; 
            options: string[]; 
            default: string[];
        }
        | { 
            controlType: 'dualInput'; 
            constraints?: NumericConstraints;
            default: number;
        }
        | { 
            controlType: 'numberInput';
            constraints?: NumericConstraints;
            default: number;
        }
        | { 
            controlType: 'checkbox';
            default: boolean;
        }
        | { 
            controlType: 'multiCheckbox';
            checkboxOptions: CheckboxOption[];
            default: Record<string, boolean>;
        }
        | { 
            controlType: 'text'; 
            default: string; 
            tokenFlag?: boolean;
        }
        | { 
            controlType: 'textbox'; 
            default: string[];
        }
        | {
            controlType: 'secureInput' | 'directoryPicker' | 'info';
            default: string;
        }
        | {
            controlType: 'infoAction';
            default: string;
            actionLabel: string;
            actionIcon?: string;
            loadingLabel?: string;
        }
    );

// Schema for defining user settings - UI metadata only
// 'default' and 'type' are derived at build time via buildUserSettingConfig
export type UserSettingSchema = Omit<SettingConfig, 'default' | 'type'> & {
    key: UserSettingPath; 
    isClientConfig: true;
};

/** SettingConfig with isClientConfig: true (distinguishes from backend options) */
export type UserSettingConfig = SettingConfig & { isClientConfig: true };

/** Returns platform-specific setting configs grouped by category */
export interface ISettingsConfigProvider {
    getSettingsConfig(): Record<string, UserSettingConfig[]>;
}

// Type guard for backend-driven select settings
export function isBackendSelect<T extends SettingConfig>(setting: T): setting is T & { 
    controlType: 'select'; 
    optionsSource: 'backend'; 
    backendOptionsConfig: BackendOptionsConfig;
    options?: SelectOption[];
} {
    return setting.controlType === 'select' && 
           'optionsSource' in setting && 
           setting.optionsSource === 'backend';
}

/** Helper function to transform ModelInfo to SelectOption */
export function transformModelsToOptions(models: ModelInfo[]): SelectOption[] {
    return models.map(model => ({
        value: model.id,
        label: model.id
    }));
}

// ============================================================================
// Section 7: Backend Types
// Raw backend setting structure from API responses
// ============================================================================

/** Backend setting raw structure with proper typing */
export interface BackendSettingRaw {
    type: BackendDataType;
    value: SettingValue;
    default: SettingValue;
    label?: string;
    description?: string;
    category?: string;
    requires_restart?: boolean;
    editable?: boolean;
    options?: SelectOption[] | string[];
    constraints?: BackendConstraints;
    optionA?: SwitchOption;
    optionB?: SwitchOption;
}

export type BackendConfigInState = Record<string, BackendSettingRaw> | null;